import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CalendarClock, ArrowRight, X } from "lucide-react";

/**
 * Slim bottom-of-viewport CTA strip that appears once the user has scrolled
 * ~1 viewport past the hero. Hidden on /contact (they're already booking)
 * and on admin/auth flows. Dismissable for the session.
 */
const HIDDEN_PATHS = [
  "/contact",
  "/scorecard",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/dashboard",
];

export default function StickyDemoBar() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem("ll_sticky_demo_dismissed") === "1"
  );

  const hiddenByRoute =
    HIDDEN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/")) ||
    pathname.startsWith("/admin");

  useEffect(() => {
    if (hiddenByRoute || dismissed) {
      setVisible(false);
      return;
    }
    const onScroll = () => {
      // Show once user scrolls past ~90% of first viewport height
      setVisible(window.scrollY > window.innerHeight * 0.9);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hiddenByRoute, dismissed]);

  const dismiss = () => {
    sessionStorage.setItem("ll_sticky_demo_dismissed", "1");
    setDismissed(true);
  };

  if (hiddenByRoute || dismissed) return null;

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 transition-transform duration-500 ease-out ${
        visible ? "translate-y-0" : "translate-y-full pointer-events-none"
      }`}
      data-testid="sticky-demo-bar"
      aria-hidden={!visible}
    >
      <div className="bg-[#0B1F33] border-t border-lime-500/40 shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.35)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center gap-3 sm:gap-4">
          <div className="hidden sm:flex w-9 h-9 flex-shrink-0 items-center justify-center rounded-full bg-lime-400/15 border border-lime-400/40">
            <CalendarClock size={18} className="text-lime-300" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold text-sm sm:text-base leading-tight truncate">
              See LumberLoop live — <span className="text-lime-300">15 minutes</span>, no slides.
            </div>
            <div className="hidden sm:block text-slate-300 text-xs mt-0.5 truncate">
              A quick walkthrough mapped to your product categories and dealer footprint.
            </div>
          </div>

          <Link
            to="/contact"
            className="btn-cta !py-2.5 !px-4 sm:!px-5 text-xs sm:text-sm whitespace-nowrap"
            data-testid="sticky-demo-cta"
            tabIndex={visible ? 0 : -1}
          >
            Book a 15-min Demo <ArrowRight size={14} />
          </Link>

          <button
            onClick={dismiss}
            aria-label="Dismiss demo banner"
            data-testid="sticky-demo-dismiss"
            tabIndex={visible ? 0 : -1}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 rounded transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
